import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicProposal() {
    const { token } = useParams();
    const [p, setP] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState(null); // null | "accept" | "decline"
    const [signatureName, setSignatureName] = useState("");
    const [agree, setAgree] = useState(false);
    const [notes, setNotes] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        axios.get(`${API}/proposals/public/${token}`, { withCredentials: true })
            .then((r) => setP(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Proposal not available."))
            .finally(() => setLoading(false));
    }, [token]);

    const submitAccept = async (e) => {
        e.preventDefault();
        if (!agree) { toast.error("You must agree to the terms."); return; }
        if (!signatureName.trim() || signatureName.trim().length < 2) { toast.error("Please type your full name."); return; }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/proposals/public/${token}/accept`, {
                signature_name: signatureName.trim(),
                agree: true,
                notes: notes || null,
            });
            setP(data);
            setMode(null);
            toast.success("Thank you — your acceptance is recorded.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not accept.");
        }
        setSubmitting(false);
    };

    const submitDecline = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/proposals/public/${token}/decline`, { reason: reason || null });
            setP(data);
            setMode(null);
            toast.success("Response recorded. Thank you.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit.");
        }
        setSubmitting(false);
    };

    if (loading) {
        return <div className="min-h-screen bg-[#fbf9f8] flex items-center justify-center"><p className="font-serif italic text-2xl text-[#0c0f3d]">Loading proposal…</p></div>;
    }
    if (error || !p) {
        return (
            <div className="min-h-screen bg-[#fbf9f8] flex items-center justify-center px-6" data-testid="proposal-error">
                <div className="max-w-[480px] text-center">
                    <h1 className="font-serif text-4xl text-[#0c0f3d]">Proposal unavailable</h1>
                    <p className="font-sans text-sm text-[#0c0f3d]/70 mt-6 leading-[1.7]">{error || "This proposal is not available or the link has expired."}</p>
                    <p className="font-sans text-xs text-[#0c0f3d]/50 mt-8">Contact <a href="mailto:shana@theconnectionguru.co" className="underline">shana@theconnectionguru.co</a> for a new link.</p>
                </div>
            </div>
        );
    }

    const isFinal = ["accepted", "declined", "expired"].includes(p.status);
    const netTotal = p.total_amount;

    return (
        <main className="min-h-screen bg-[#fbf9f8] py-16 px-6" data-testid="public-proposal-page">
            <div className="max-w-[880px] mx-auto">
                {p.admin_preview && (
                    <div className="mb-6 bg-[#0c0f3d] text-[#fbf9f8] px-6 py-4 flex items-center justify-between" data-testid="admin-preview-banner">
                        <div>
                            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#ffb5ab]">Admin preview</p>
                            <p className="font-sans text-sm text-[#fbf9f8]/80 mt-1">This is exactly what your client will see. Merge tokens are filled from the current proposal data. Nothing is logged (no viewed_at, no expiry flip).</p>
                        </div>
                    </div>
                )}
                {/* Header */}
                <div className="bg-[#0c0f3d] text-[#fbf9f8] p-10 md:p-14">
                    <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#ffb5ab]">Proposal</p>
                    <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.15]">{p.title}</h1>
                    <p className="font-sans text-sm text-[#fbf9f8]/70 mt-6">Prepared for {p.client_name || p.client_organization || "you"}{p.client_organization && p.client_name ? ` · ${p.client_organization}` : ""}</p>
                    <p className="font-sans text-xs text-[#fbf9f8]/50 mt-1">{p.prepared_by}</p>
                    {p.expires_at && !isFinal && (
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#ffb5ab] mt-6">Expires {new Date(p.expires_at).toLocaleDateString()}</p>
                    )}
                </div>

                <div className="bg-white border border-t-0 border-[#e3c7b9] p-10 md:p-14 space-y-10">
                    {p.intro && (
                        <section>
                            <p className="font-serif text-lg text-[#0c0f3d] leading-[1.8] whitespace-pre-line">{p.intro}</p>
                        </section>
                    )}

                    {p.scope_items?.length > 0 && (
                        <section>
                            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a] mb-4">Scope of work</p>
                            <ul className="space-y-3">
                                {p.scope_items.map((s, i) => (
                                    <li key={i} className="font-serif text-lg text-[#0c0f3d] pl-6 relative">
                                        <span className="absolute left-0 top-2 w-3 h-[1px] bg-[#e47a6a]"></span>
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <section>
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a] mb-4">Investment</p>
                        <div className="border border-[#e3c7b9]">
                            {p.line_items.map((li, i) => (
                                <div key={i} className="grid grid-cols-[1fr_80px_120px] gap-4 p-4 border-b border-[#e3c7b9] last:border-0 items-center">
                                    <p className="font-serif text-base text-[#0c0f3d]">{li.description}</p>
                                    <p className="font-sans text-xs text-[#0c0f3d]/60 text-center">× {li.quantity}</p>
                                    <p className="font-serif text-lg text-[#0c0f3d] text-right">{fmtMoney((li.amount * li.quantity))}</p>
                                </div>
                            ))}
                            <div className="p-4 bg-[#fbf9f8] flex justify-between items-center">
                                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#0c0f3d]">Total investment</p>
                                <p className="font-serif text-2xl text-[#0c0f3d]">{fmtMoney(netTotal)}</p>
                            </div>
                            {p.deposit_percentage > 0 && (
                                <div className="p-4 bg-[#ffb5ab]/20 border-t border-[#e3c7b9] flex justify-between items-center">
                                    <div>
                                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a]">Deposit due on acceptance ({p.deposit_percentage}%)</p>
                                        <p className="font-sans text-xs text-[#0c0f3d]/60 mt-1">Balance {fmtMoney((netTotal - p.deposit_amount))} invoiced upon delivery.</p>
                                    </div>
                                    <p className="font-serif text-xl text-[#e47a6a]">{fmtMoney(p.deposit_amount)}</p>
                                </div>
                            )}
                        </div>
                    </section>

                    {p.terms && (
                        <section>
                            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a] mb-4">Terms &amp; conditions</p>
                            <p className="font-serif text-sm text-[#0c0f3d]/80 leading-[1.8] whitespace-pre-line">{p.terms}</p>
                        </section>
                    )}

                    {/* Status banner */}
                    {p.status === "accepted" && (
                        <div className="bg-[#4a7c59]/10 border border-[#4a7c59]/30 p-6 flex items-start gap-4" data-testid="proposal-accepted-banner">
                            <CheckCircle2 size={24} className="text-[#4a7c59] shrink-0 mt-1" />
                            <div>
                                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#4a7c59]">Accepted</p>
                                <p className="font-serif text-xl text-[#0c0f3d] mt-1">Thank you, {p.signature_name}.</p>
                                <p className="font-sans text-sm text-[#0c0f3d]/70 mt-2">Signed on {new Date(p.accepted_at).toLocaleString()}. Shana will follow up with next steps and the invoice.</p>
                            </div>
                        </div>
                    )}
                    {p.status === "declined" && (
                        <div className="bg-[#e47a6a]/10 border border-[#e47a6a]/30 p-6 flex items-start gap-4" data-testid="proposal-declined-banner">
                            <XCircle size={24} className="text-[#e47a6a] shrink-0 mt-1" />
                            <div>
                                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a]">Declined</p>
                                <p className="font-serif text-xl text-[#0c0f3d] mt-1">Response recorded.</p>
                                <p className="font-sans text-sm text-[#0c0f3d]/70 mt-2">Thank you for taking the time to review. Reach out anytime.</p>
                            </div>
                        </div>
                    )}
                    {p.status === "expired" && (
                        <div className="bg-[#b59b7c]/10 border border-[#b59b7c]/30 p-6" data-testid="proposal-expired-banner">
                            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#b59b7c]">Expired</p>
                            <p className="font-serif text-xl text-[#0c0f3d] mt-1">This proposal has expired.</p>
                            <p className="font-sans text-sm text-[#0c0f3d]/70 mt-2">Please email <a className="underline" href="mailto:shana@theconnectionguru.co">shana@theconnectionguru.co</a> for a refreshed version.</p>
                        </div>
                    )}

                    {/* Action */}
                    {!isFinal && !mode && !p.admin_preview && (
                        <section className="pt-6 border-t border-[#e3c7b9] flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={() => setMode("accept")}
                                data-testid="proposal-accept-btn"
                                className="px-8 py-4 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] flex-1 flex items-center justify-center gap-2"
                            ><PenLine size={14} /> Accept &amp; sign</button>
                            <button
                                onClick={() => setMode("decline")}
                                data-testid="proposal-decline-btn"
                                className="px-8 py-4 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white flex-1"
                            >Decline</button>
                        </section>
                    )}

                    {/* Accept form */}
                    {mode === "accept" && (
                        <form onSubmit={submitAccept} className="pt-6 border-t border-[#e3c7b9] space-y-5" data-testid="proposal-accept-form">
                            <div>
                                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a] mb-2">Your acceptance</p>
                                <p className="font-serif text-base text-[#0c0f3d] leading-[1.7]">By typing your full legal name below, you agree to the scope, investment, and terms outlined in this proposal. This constitutes a binding electronic signature.</p>
                            </div>
                            <div>
                                <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#6d91ad]">Your full name</label>
                                <input
                                    value={signatureName}
                                    onChange={(e) => setSignatureName(e.target.value)}
                                    placeholder="Type your full name"
                                    required
                                    data-testid="proposal-signature-input"
                                    className="w-full mt-2 px-4 py-3 border border-[#e3c7b9] font-serif text-2xl text-[#0c0f3d] italic outline-none focus:border-[#0c0f3d] bg-[#fbf9f8]"
                                />
                            </div>
                            <div>
                                <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#6d91ad]">Note (optional)</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                    placeholder="Anything you'd like Shana to know."
                                    data-testid="proposal-accept-notes"
                                    className="w-full mt-2 px-4 py-3 border border-[#e3c7b9] font-serif text-sm text-[#0c0f3d] outline-none focus:border-[#0c0f3d]"
                                />
                            </div>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="proposal-agree-checkbox" className="mt-1 accent-[#0c0f3d]" />
                                <span className="font-sans text-sm text-[#0c0f3d]/80 leading-[1.6]">I have read and agree to the scope, investment, and terms in this proposal, and I confirm my typed name above is my electronic signature.</span>
                            </label>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setMode(null)} className="px-6 py-3 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white">Back</button>
                                <button type="submit" disabled={submitting} data-testid="proposal-accept-submit" className="flex-1 px-8 py-3 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] disabled:opacity-50">
                                    {submitting ? "Signing…" : "Sign & accept"}
                                </button>
                            </div>
                        </form>
                    )}

                    {mode === "decline" && (
                        <form onSubmit={submitDecline} className="pt-6 border-t border-[#e3c7b9] space-y-5" data-testid="proposal-decline-form">
                            <div>
                                <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#6d91ad]">Reason (optional)</label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={4}
                                    placeholder="Help Shana understand — timing, budget, scope, or anything else."
                                    data-testid="proposal-decline-reason"
                                    className="w-full mt-2 px-4 py-3 border border-[#e3c7b9] font-serif text-sm text-[#0c0f3d] outline-none focus:border-[#0c0f3d]"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setMode(null)} className="px-6 py-3 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white">Back</button>
                                <button type="submit" disabled={submitting} data-testid="proposal-decline-submit" className="flex-1 px-8 py-3 bg-[#e47a6a] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] disabled:opacity-50">
                                    {submitting ? "Sending…" : "Submit response"}
                                </button>
                            </div>
                        </form>
                    )}
                    {p.admin_preview && !isFinal && (
                        <div className="pt-6 border-t border-[#e3c7b9] bg-[#fbf9f8] -mx-10 md:-mx-14 px-10 md:px-14 py-6" data-testid="admin-preview-actions-disabled">
                            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#6d91ad] mb-2">Client actions</p>
                            <p className="font-serif text-lg text-[#0c0f3d]">Accept &amp; Decline buttons appear here for your client.</p>
                            <p className="font-sans text-xs text-[#0c0f3d]/60 mt-1">They are hidden in your admin preview so you can&rsquo;t accidentally sign your own proposal.</p>
                        </div>
                    )}

                </div>

                <p className="text-center font-sans text-xs text-[#0c0f3d]/50 mt-8">
                    Prepared with care by Shana Price · The Connection Guru®
                </p>
                <div className="text-center mt-4">
                    <a
                        href={`${API}/proposals/public/${token}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="proposal-download-pdf"
                        className="inline-flex items-center gap-2 text-xs font-sans uppercase tracking-[0.2em] text-[#6d91ad] hover:text-[#0c0f3d]"
                    >Download proposal as PDF</a>
                </div>
            </div>
        </main>
    );
}
